'use client'

import { useState, useCallback } from 'react'
import { ArrowRight, ChevronDown } from '@/components/ui/icons'
import { Link } from '@/i18n/navigation'
import { useExtracted } from 'next-intl'

export interface PricingFAQItem {
  id: string
  question: string
  answer: string
  /** An optional call to action under the answer (internal route). */
  link?: { label: string; href: string }
}

interface PricingFAQProps {
  faqs: PricingFAQItem[]
}

export function PricingFAQ({ faqs }: PricingFAQProps) {
  const t = useExtracted()
  const [openId, setOpenId] = useState<string | null>(null)

  const toggleFAQ = useCallback((id: string) => {
    setOpenId(current => (current === id ? null : id))
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleFAQ(id)
      }
    },
    [toggleFAQ],
  )

  return (
    <section className="py-12" aria-labelledby="pricing-faq-heading">
      <h2 id="pricing-faq-heading" className="mb-8 text-3xl font-bold tracking-tight md:text-4xl">
        {t('Frequently Asked Questions')}
      </h2>
      <div className="space-y-3" role="list">
        {faqs.map(faq => {
          const isOpen = openId === faq.id
          return (
            <div
              key={faq.id}
              id={faq.id}
              className="rounded-xl border border-white/10 bg-white/[0.02] transition-colors"
              role="listitem"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                onKeyDown={e => handleKeyDown(e, faq.id)}
                aria-expanded={isOpen}
                aria-controls={`${faq.id}-content`}
                className="flex w-full cursor-pointer items-center justify-between px-6 py-4 text-start text-base font-medium text-white md:text-lg"
              >
                <span className="pe-4">{faq.question}</span>
                <ChevronDown
                  className={`h-5 w-5 flex-shrink-0 text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={`${faq.id}-content`}
                role="region"
                aria-labelledby={faq.id}
                className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-4 text-sm leading-relaxed text-white/70 md:text-base">{faq.answer}</p>
                  {faq.link && (
                    <Link
                      href={faq.link.href}
                      className="group/link mb-4 ms-6 inline-flex items-center gap-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white"
                    >
                      {faq.link.label}
                      <ArrowRight
                        strokeWidth={1.5}
                        className="h-3.5 w-3.5 transition-transform duration-200 group-hover/link:translate-x-0.5"
                      />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
